import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import Button from '../../components/Button';
import FormField, { Input, Textarea, Select } from '../../components/FormField';
import { FORMAT_DESCRIPTIONS } from '../../utils/tournamentFormats';

const EMPTY = {
  event_id: '', name: '', game: '', start_time: '',
  description: '', rules: '', status: 'open',
  format: 'single_elimination', team_size: 1, max_teams: '', third_place_match: true,
  category: 'main', ffa_quali_rounds: 3, team_assignment_mode: 'self_organized',
};
const toInputDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};
const STATUSES = ['open', 'in_progress', 'finished', 'cancelled'];
const STATUS_LABELS = { open: 'Offen', in_progress: 'Läuft', finished: 'Beendet', cancelled: 'Abgesagt' };

const GAME_PRESETS = [
  'Counter-Strike 2', 'PUBG', 'Battlefield', 'Call of Duty', 'Valorant',
  'League of Legends', 'Rocket League', 'FIFA / EA FC', 'Fortnite', 'Minecraft',
];

export default function AdminTournaments() {
  const [events, setEvents]       = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [tournaments, setTournaments] = useState([]);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [participants, setParticipants] = useState({});
  const [openParticipants, setOpenParticipants] = useState(null);

  useEffect(() => { api.get('/api/events/all').then((r) => setEvents(r.data)); }, []);

  const loadTournaments = () => {
    if (!selectedEvent) return;
    api.get(`/api/events/${selectedEvent}/tournaments`).then((r) => setTournaments(r.data));
  };

  useEffect(() => { loadTournaments(); }, [selectedEvent]); // eslint-disable-line

  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const openNew = () => {
    setForm({ ...EMPTY, event_id: parseInt(selectedEvent) });
    setEditing('new'); setError('');
  };

  const openEdit = (t) => {
    setForm({ ...t, start_time: toInputDate(t.start_time), game: t.game || '', description: t.description || '', rules: t.rules || '',
      team_assignment_mode: t.team_assignment_mode || 'self_organized' });
    setEditing(t.id); setError('');
  };

  const save = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const payload = {
        ...form,
        event_id: parseInt(selectedEvent),
        team_size: parseInt(form.team_size) || 1,
        max_teams: form.max_teams ? parseInt(form.max_teams) : null,
        start_time: form.start_time || null,
        game: form.game || null,
        description: form.description || null,
        rules: form.rules || null,
        third_place_match: Boolean(form.third_place_match),
        ffa_quali_rounds: form.format === 'ffa_scoreboard' ? parseInt(form.ffa_quali_rounds) || 3 : null,
        team_assignment_mode: form.team_assignment_mode || 'self_organized',
      };
      if (editing === 'new') {
        await api.post('/api/admin/tournaments', payload);
      } else {
        await api.put(`/api/admin/tournaments/${editing}`, payload);
      }
      setEditing(null); loadTournaments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    } finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Turnier wirklich löschen?')) return;
    await api.delete(`/api/admin/tournaments/${id}`);
    loadTournaments();
  };

  const loadParticipants = async (id) => {
    if (openParticipants === id) { setOpenParticipants(null); return; }
    const res = await api.get(`/api/admin/tournaments/${id}/participants`);
    setParticipants((p) => ({ ...p, [id]: res.data }));
    setOpenParticipants(id);
  };

  const removeParticipant = async (tournamentId, userId) => {
    await api.delete(`/api/admin/tournaments/${tournamentId}/participants/${userId}`);
    loadParticipants(tournamentId);
  };

  return (
    <div>
      <h2 className="page-title">Turniere</h2>

      <FormField label="Event auswählen" className="max-w-sm mb-6">
        <Select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)}>
          <option value="">– Event wählen –</option>
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </Select>
      </FormField>

      {selectedEvent && (
        <>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">{tournaments.length} Turnier(e)</p>
            <Button onClick={openNew} size="sm">+ Turnier hinzufügen</Button>
          </div>

          {/* Form */}
          {editing && (
            <div className="card border border-ocean-200 mb-6">
              <h3 className="section-title">{editing === 'new' ? 'Neues Turnier' : 'Turnier bearbeiten'}</h3>
              {error && <div className="alert-error mb-4">{error}</div>}
              <form onSubmit={save}>
                <div className="grid grid-cols-2 gap-x-4">
                  <FormField label="Name" required className="col-span-2">
                    <Input value={form.name} onChange={set('name')} required placeholder="z.B. CS2 – 5v5" />
                  </FormField>

                  <FormField label="Spiel">
                    <Select value={form.game} onChange={set('game')}>
                      <option value="">– Spiel wählen oder eingeben –</option>
                      {GAME_PRESETS.map((g) => <option key={g} value={g}>{g}</option>)}
                    </Select>
                  </FormField>
                  <FormField label="Oder eigene Eingabe">
                    <Input value={form.game} onChange={set('game')} placeholder="Eigenes Spiel..." />
                  </FormField>

                  <FormField label="Startzeit (optional)">
                    <Input type="datetime-local" value={form.start_time} onChange={set('start_time')} />
                  </FormField>

                  <FormField label="Status">
                    <Select value={form.status} onChange={set('status')}>
                      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </Select>
                  </FormField>

                  <FormField label="Format" required>
                    <Select value={form.format} onChange={set('format')}>
                      <option value="single_elimination">Single Elimination</option>
                      <option value="double_elimination">Double Elimination</option>
                      <option value="ffa_scoreboard">FFA-Scoreboard</option>
                      <option value="seeded_single_elimination">Seeded Single Elimination</option>
                      <option value="bingo">Bingo</option>
                      <option value="battle_royale">Battle Royale (PUBG)</option>
                      <option value="time_trial">Zeitfahren</option>
                    </Select>
                    {FORMAT_DESCRIPTIONS[form.format] && (
                      <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                        {FORMAT_DESCRIPTIONS[form.format]}
                      </p>
                    )}
                  </FormField>
                  <FormField label="Kategorie">
                    <Select value={form.category} onChange={set('category')}>
                      <option value="main">Haupt-Turnier</option>
                      <option value="fun">Fun-Turnier</option>
                    </Select>
                  </FormField>
                  {!['bingo', 'time_trial'].includes(form.format) && (
                    <>
                      <FormField label="Spieler pro Team (1–5)" required>
                        <Input type="number" min="1" max="5" value={form.team_size} onChange={set('team_size')} required />
                      </FormField>
                      <FormField label="Max. Teams (optional)">
                        <Input type="number" min="2" value={form.max_teams} onChange={set('max_teams')} placeholder="Unbegrenzt" />
                      </FormField>
                      <FormField label="Team-Zuteilung" className="col-span-2">
                        <Select value={form.team_assignment_mode} onChange={set('team_assignment_mode')}>
                          <option value="self_organized">Teilnehmer bilden Teams selbst</option>
                          <option value="admin_assigned">Admin teilt Teilnehmer nach Anmeldung in Teams ein</option>
                        </Select>
                        <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                          {form.team_assignment_mode === 'admin_assigned'
                            ? 'Teilnehmer melden sich einzeln an, ohne eigenes Team zu wählen. Der Admin teilt sie anschließend im Turnier-Detail (Tab „Teams") den Teams zu. Bestehende, bereits selbst gebildete Teams bleiben dabei unangetastet.'
                            : 'Teilnehmer erstellen selbst ein Team oder treten einem bestehenden Team bei (Standardverhalten).'}
                        </p>
                      </FormField>
                      <FormField label="Spiel um Platz 3">
                        <Select value={form.third_place_match ? 'true' : 'false'}
                          onChange={(e) => setForm({ ...form, third_place_match: e.target.value === 'true' })}>
                          <option value="true">Ja</option>
                          <option value="false">Nein</option>
                        </Select>
                      </FormField>
                      {form.format === 'ffa_scoreboard' && (
                        <FormField label="Qualifikationsrunden (FFA)" required>
                          <Input type="number" min="1" max="10" value={form.ffa_quali_rounds}
                            onChange={set('ffa_quali_rounds')} required />
                        </FormField>
                      )}
                    </>
                  )}
                </div>

                <FormField label="Beschreibung (optional)">
                  <Textarea value={form.description} onChange={set('description')} placeholder="Modus, Regeln, Preise..." />
                </FormField>

                <FormField label="Spielregeln/Servereinstellungen (optional)">
                  <Textarea value={form.rules} onChange={set('rules')} placeholder="Serverdaten, Regelwerk, Einstellungen..." />
                </FormField>

                <div className="flex gap-2">
                  <Button type="submit" loading={loading}>Speichern</Button>
                  <Button variant="ghost" type="button" onClick={() => setEditing(null)}>Abbrechen</Button>
                </div>
              </form>
            </div>
          )}

          {/* Tournament list */}
          {tournaments.length === 0 && !editing && (
            <div className="card text-center py-8 text-slate-400 text-sm">
              Noch keine Turniere für dieses Event.
            </div>
          )}

          <div className="space-y-3">
            {tournaments.map((t) => (
              <div key={t.id} className="card">
                <div className="flex flex-wrap justify-between items-start gap-3 mb-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-ocean-900">{t.name}</span>
                      {t.game && (
                        <span className="text-xs font-medium bg-ocean-50 text-ocean-600 border border-ocean-200 px-2 py-0.5 rounded">
                          {t.game}
                        </span>
                      )}
                      <TournamentStatusBadge status={t.status} />
                      <CategoryBadge category={t.category} />
                      {t.team_assignment_mode === 'admin_assigned' && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                          Admin teilt Teams zu
                        </span>
                      )}
                    </div>
                    <div className="flex gap-4 mt-1 text-xs text-slate-500 flex-wrap">
                      {t.start_time && <span>Start: {new Date(t.start_time).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                      <span>{t.format === 'double_elimination' ? 'Double Elim' : 'Single Elim'}</span>
                      {t.team_size > 1 && <span>{t.team_size}v{t.team_size}</span>}
                      <span className="font-medium text-ocean-600">{t.teams_full ?? t.participant_count ?? 0} vollst. Teams</span>
                      {t.bracket_generated && <span className="text-ocean-500 font-bold">Bracket aktiv</span>}
                    </div>
                    {t.description && <p className="text-xs text-slate-400 mt-1 line-clamp-1">{t.description}</p>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Link to={`/admin/tournaments/${t.id}`}>
                      <Button variant="ghost" size="sm">Details</Button>
                    </Link>
                    <Button variant="ghost" size="sm" onClick={() => loadParticipants(t.id)}>
                      {openParticipants === t.id ? 'Schließen' : 'Teilnehmer'}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => openEdit(t)}>Bearbeiten</Button>
                    <Button variant="danger" size="sm" onClick={() => del(t.id)}>Löschen</Button>
                  </div>
                </div>

                {/* Participant progress bar */}
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-ocean-500 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (t.participant_count / t.max_participants) * 100)}%` }} />
                </div>

                {/* Participants list */}
                {openParticipants === t.id && (
                  <div className="mt-3 border-t border-slate-100 pt-3">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Angemeldete Teilnehmer</p>
                    {(participants[t.id] || []).length === 0 ? (
                      <p className="text-xs text-slate-400">Noch keine Anmeldungen.</p>
                    ) : (
                      <div className="space-y-1">
                        {(participants[t.id] || []).map((p) => (
                          <div key={p.id} className="flex justify-between items-center text-sm py-1 border-b border-slate-50">
                            <span>
                              <span className="font-medium text-ocean-800">{p.username}</span>
                              {p.team_name && <span className="text-slate-400 ml-2">({p.team_name})</span>}
                            </span>
                            <Button variant="danger" size="sm" onClick={() => removeParticipant(t.id, p.user_id)}>
                              Entfernen
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CategoryBadge({ category }) {
  if (!category || category === 'main') return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded border bg-sand-50 text-sand-700 border-sand-200">
      Haupt
    </span>
  );
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-200">
      Fun
    </span>
  );
}

function TournamentStatusBadge({ status }) {
  const styles = {
    open:        'bg-emerald-50 text-emerald-700 border-emerald-200',
    in_progress: 'bg-ocean-50 text-ocean-700 border-ocean-200',
    finished:    'bg-slate-100 text-slate-500 border-slate-200',
    cancelled:   'bg-red-50 text-red-600 border-red-200',
  };
  const labels = { open: 'Offen', in_progress: 'Läuft', finished: 'Beendet', cancelled: 'Abgesagt' };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${styles[status] || styles.open}`}>
      {labels[status] || status}
    </span>
  );
}
